import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router'

import Main from './pages/main'
import Login from './pages/login'
import PrivateRoute from './utils/private-route'
import {
  CHANGE_PASSWORD_PATH,
  selectMustChangePassword,
  selectToken
} from './pages/login/authSlice'
import ChangePasswordPage from './pages/change-password'
import SchemaLoader from './pages/schema-loader'
import MyProfile from './pages/my-profile'
import DevicesPage from './pages/devices'
import { lazyWithRetry } from '@/utils/lazyRetry'
import NotFoundPage from './pages/not-found'

const ChannelsPage = lazy(() => import('./pages/channels'))
const UsersPage = lazy(() => import('./pages/users'))
const PartnersPage = lazy(() => import('./pages/partners'))
const MonitoringPage = lazy(() => import('./pages/monitoring'))
const PartnerInside = lazy(() => import('./pages/partner-inside'))
const AdditionalPage = lazy(() => import('./pages/additional'))
const AbonementsPage = lazy(() => import('./pages/abonements'))
const EmployeesPage = lazy(() => import('./pages/employees'))
const OrangesPage = lazy(() => import('./pages/oranges'))
const DevicePanelPage = lazy(() => import('./pages/device-panel'))
const SbcDetailPage = lazy(() => import('./pages/device-panel/sbc-detail'))
const SystemSettingsPage = lazy(() => import('./pages/system-settings'))
const ReportsPage = lazyWithRetry(() => import('./pages/reports'), 'reports')
const EntriesHistoryPage = lazy(() => import('./pages/entries-history'))

const getSafeFromPath = (pathname) => {
  if (!pathname || pathname === '/login') return '/'
  return pathname
}

const MainRoutes = () => {
  const token = useSelector(selectToken)
  const mustChangePassword = useSelector(selectMustChangePassword)
  const location = useLocation()
  const from = getSafeFromPath(location?.state?.from?.pathname)
  const authorizedHome = mustChangePassword ? CHANGE_PASSWORD_PATH : from
  return (
    <Suspense fallback={<SchemaLoader />}>
      <Routes>
        <Route
          path="/login"
          element={token ? <Navigate to={authorizedHome} replace /> : <Login />}
        />
        <Route
          path={CHANGE_PASSWORD_PATH}
          element={
            !token ? (
              <Navigate to="/login" replace />
            ) : mustChangePassword ? (
              <ChangePasswordPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<Main />} />
          <Route path="/channels" element={<ChannelsPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/additional" element={<AdditionalPage />} />
          <Route path="/profile" element={<MyProfile />} />
          <Route path="/partners">
            <Route index element={<PartnersPage />} />
            <Route path=":partnerId">
              <Route index element={<PartnerInside />} />
            </Route>
          </Route>
          <Route path="/monitoring/:buildingId" element={<MonitoringPage />} />
          <Route path="/users/*" element={<UsersPage />} />
          <Route path="/roles" element={<Navigate to="/users/roles" replace />} />
          <Route path="/permissions" element={<Navigate to="/users/permissions" replace />} />
          <Route path="/abonements" element={<AbonementsPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/oranges" element={<OrangesPage />} />
          <Route path="/device-panel" element={<DevicePanelPage />} />
          <Route path="/device-panel/sbcs/:sbcId" element={<SbcDetailPage />} />
          <Route path="/system-settings" element={<SystemSettingsPage />} />
          <Route path="/reports/*" element={<ReportsPage />} />
          <Route path="/entries-history" element={<EntriesHistoryPage />} />
          { token ? <Route path="*" element={<NotFoundPage />} /> : null }
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

export default MainRoutes
